import React, { useState, useEffect, useRef } from 'react';
import { FiSave, FiX, FiPlus, FiEdit2 } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './editSales.css';

const unitMultiplier = (unit) => (unit === 'set' ? 6 : unit === 'dozen' ? 12 : 1);

const EditSales = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [salesItems, setSalesItems] = useState([]); // { sale_id?, item_id, item_name, quantity, unit, selling_price }
  const [retailerName, setRetailerName] = useState(null);
  const [customerName, setCustomerName] = useState(null);

  const [currentItem, setCurrentItem] = useState({
    productId: '',
    quantity: 1,
    unit: 'piece',
    sellingPrice: '',
    calculatedPrice: '',
    productSearch: '',
    showDropdown: false
  });

  const [formData, setFormData] = useState({
    paidAmount: '',
    paymentType: 'cash',
    bankAccountId: '',
    discountType: 'amount',
    discountValue: ''
  });

  const [bankAccounts, setBankAccounts] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invoiceRes, productsRes, bankAccountsRes] = await Promise.all([
          axios.get(`/sales/invoice/${invoiceId}`),
          axios.get('/items/all'),
          axios.get('/bank-accounts')
        ]);

        const invoice = invoiceRes.data;

        setProducts(productsRes.data.data.map(item => ({
          id: item.item_id,
          name: item.item_name,
          costPrice: item.cost_price,
          sellingPrice: item.selling_price,
          stock: item.quantity,
          unit: item.unit || 'piece'
        })));

        setBankAccounts(Array.isArray(bankAccountsRes.data) ? bankAccountsRes.data : []);

        setSalesItems((invoice.items || []).map(item => ({
          sale_id: item.sale_id,
          item_id: item.item_id,
          item_name: item.item_name,
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          selling_price: parseFloat(item.selling_price)
        })));

        setRetailerName(invoice.retailer_name || null);
        setCustomerName(invoice.customer_name || null);

        setFormData({
          paidAmount: invoice.paid_amount,
          paymentType: invoice.payment_type || 'cash',
          bankAccountId: invoice.bank_account_id || '',
          discountType: invoice.discount_type || 'amount',
          discountValue: parseFloat(invoice.discount_value) > 0 ? invoice.discount_value : ''
        });
      } catch (err) {
        console.error('Error loading invoice for edit:', err);
        setApiError(err.response?.data?.error || 'Failed to load invoice for editing');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [invoiceId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setCurrentItem(prev => ({ ...prev, showDropdown: false }));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredProducts = products.filter(product => {
    const searchTerm = currentItem.productSearch.toLowerCase();
    return product.name.toLowerCase().includes(searchTerm) ||
           product.id.toString().includes(searchTerm);
  });

  const handleCurrentItemChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem({
      ...currentItem,
      [name]: value,
      showDropdown: name === 'productSearch' && value.length > 0
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'paymentType' && value !== 'bank' ? { bankAccountId: '' } : {})
    }));
  };

  const selectProduct = (product) => {
    if (product.stock <= 0) return;
    const basePrice = Number(product.sellingPrice) || 0;
    const unit = product.unit || 'piece';
    const calculatedPrice = basePrice * unitMultiplier(unit);

    setCurrentItem({
      productId: product.id,
      productSearch: `${product.name} `,
      quantity: 1,
      unit,
      sellingPrice: basePrice,
      calculatedPrice,
      showDropdown: false
    });
  };

  const getUnitOptions = (productUnit) => {
    if (productUnit === 'piece') return ['piece'];
    if (productUnit === 'kg') return ['kg'];
    if (productUnit === 'set') return ['set', 'piece'];
    if (productUnit === 'dozen') return ['dozen', 'piece'];
    return ['piece'];
  };

  const handleUnitChange = (e) => {
    const newUnit = e.target.value;
    const basePrice = currentItem.sellingPrice || 0;
    setCurrentItem(prev => ({
      ...prev,
      unit: newUnit,
      calculatedPrice: basePrice * unitMultiplier(newUnit)
    }));
  };

  const addSalesItem = () => {
    const product = products.find(p => p.id === currentItem.productId);
    if (!product) return;

    setSalesItems(prev => [...prev, {
      item_id: currentItem.productId,
      item_name: product.name,
      quantity: parseFloat(currentItem.quantity),
      unit: currentItem.unit,
      selling_price: parseFloat(currentItem.sellingPrice)
    }]);

    setCurrentItem({
      productId: '',
      quantity: 1,
      unit: 'piece',
      sellingPrice: '',
      calculatedPrice: '',
      productSearch: '',
      showDropdown: false
    });
  };

  const removeSalesItem = (index) => {
    setSalesItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateLineQuantity = (index, value) => {
    setSalesItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], quantity: value };
      return next;
    });
  };

  const updateLineNormalizedPrice = (index, value) => {
    setSalesItems(prev => {
      const next = [...prev];
      const multiplier = unitMultiplier(next[index].unit);
      next[index] = { ...next[index], selling_price: value === '' ? '' : parseFloat(value) / multiplier };
      return next;
    });
  };

  const calculateTotal = () => {
    return salesItems.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.selling_price) || 0;
      return sum + price * qty * unitMultiplier(item.unit);
    }, 0);
  };

  const calculateDiscountAmount = () => {
    const subtotal = calculateTotal();
    const value = parseFloat(formData.discountValue) || 0;
    const raw = formData.discountType === 'percentage'
      ? subtotal * value / 100
      : value;
    const clamped = Math.min(Math.max(raw, 0), subtotal);
    return Math.round(clamped * 100) / 100;
  };

  const calculateNetTotal = () => {
    return calculateTotal() - calculateDiscountAmount();
  };

  const validateForm = () => {
    const newErrors = {};

    if (salesItems.length === 0) {
      newErrors.salesItems = 'At least one product is required';
    }

    salesItems.forEach((item, index) => {
      if (!item.quantity || isNaN(item.quantity) || parseFloat(item.quantity) <= 0) {
        newErrors[`quantity_${index}`] = 'Enter a valid quantity';
      }
      if (item.selling_price === '' || isNaN(item.selling_price) || parseFloat(item.selling_price) <= 0) {
        newErrors[`price_${index}`] = 'Enter a valid price';
      }
    });

    const total = calculateNetTotal();
    if (formData.paidAmount === '' || isNaN(formData.paidAmount)) {
      newErrors.paidAmount = 'Paid amount is required';
    } else if (parseFloat(formData.paidAmount) < 0) {
      newErrors.paidAmount = 'Paid amount cannot be negative';
    } else if (parseFloat(formData.paidAmount) > total) {
      newErrors.paidAmount = 'Paid amount cannot exceed total amount';
    }

    if (!retailerName && !customerName && total > parseFloat(formData.paidAmount || 0)) {
      newErrors.paidAmount = 'This walk-in sale has no customer name on file — paid amount must cover the full total';
    }

    if (formData.paymentType === 'bank' && parseFloat(formData.paidAmount || 0) > 0 && !formData.bankAccountId) {
      newErrors.bankAccountId = 'Please select a bank account';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      await axios.put(`/sales/${invoiceId}`, {
        paid_amount: parseFloat(formData.paidAmount),
        payment_type: formData.paymentType,
        bank_account_id: formData.paymentType === 'bank' && parseFloat(formData.paidAmount || 0) > 0
          ? parseInt(formData.bankAccountId)
          : null,
        discount_type: formData.discountType,
        discount_value: parseFloat(formData.discountValue) || 0,
        items: salesItems.map(item => ({
          sale_id: item.sale_id || undefined,
          item_id: parseInt(item.item_id),
          quantity: parseInt(item.quantity),
          selling_price: parseFloat(item.selling_price),
          unit: item.unit
        }))
      });

      navigate('/sales');
    } catch (err) {
      console.error('Error updating sale:', err);
      setApiError(err.response?.data?.error || 'Failed to update sale');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="loading">Loading sale...</div>;
  }

  return (
    <div className="add-sales-container">
      <div className="add-sales-header">
        <h1 className="add-sales-title">Edit Sale</h1>
        <button
          className="cancel-btn"
          onClick={() => navigate('/sales')}
          disabled={isSubmitting}
        >
          <FiX /> Cancel
        </button>
      </div>

      {apiError && (
        <div className="api-error-message" style={{ marginBottom: '1rem' }}>
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="sales-form">
        <div className="form-section">
          <h2 className="section-title">Customer</h2>
          <p>{retailerName || customerName || 'Walk-in customer'}</p>
        </div>

        <div className="form-section">
          <h2 className="section-title">Add / Correct Products</h2>
          <div className="product-search" ref={searchRef}>
            <div className={`form-group ${errors.salesItems ? 'has-error' : ''}`}>
              <label htmlFor="productSearch">Search Product to Add</label>
              <input
                type="text"
                id="productSearch"
                name="productSearch"
                value={currentItem.productSearch}
                onChange={handleCurrentItemChange}
                placeholder="Type product name or ID..."
                autoComplete="off"
                disabled={isSubmitting}
              />
              {errors.salesItems && <span className="error-message">{errors.salesItems}</span>}
            </div>

            {currentItem.showDropdown && filteredProducts.length > 0 && (
              <div className="product-dropdown">
                {filteredProducts.map(product => {
                  const outOfStock = product.stock <= 0;
                  return (
                    <div
                      key={product.id}
                      className={`product-option ${currentItem.productId === product.id ? 'selected' : ''} ${outOfStock ? 'out-of-stock' : ''}`}
                      onClick={() => !outOfStock && selectProduct(product)}
                      aria-disabled={outOfStock}
                    >
                      <div className="product-name">{product.name}</div>
                      <div className="product-details">
                        {outOfStock ? (
                          <span className="stock-warning">0 left in stock</span>
                        ) : (
                          <span>Stock: {product.stock}</span>
                        )}
                        <span>Price: Rs{product.sellingPrice.toLocaleString('en-IN')}/pc</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {currentItem.productId && (
            <div className="product-details-container">
              <div className="product-details">
                <div className="form-group">
                  <label htmlFor="unit">Unit *</label>
                  <select
                    id="unit"
                    name="unit"
                    value={currentItem.unit}
                    onChange={handleUnitChange}
                    disabled={isSubmitting}
                  >
                    {getUnitOptions(products.find(p => p.id === currentItem.productId)?.unit || 'piece')
                      .map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="quantity">Quantity *</label>
                  <input
                    type="number"
                    id="quantity"
                    name="quantity"
                    value={currentItem.quantity}
                    onChange={handleCurrentItemChange}
                    min="1"
                    onWheel={(e) => e.target.blur()}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sellingPrice">Price</label>
                  <input
                    type="number"
                    id="sellingPrice"
                    name="sellingPrice"
                    value={currentItem.calculatedPrice}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      if (rawValue === '') {
                        setCurrentItem(prev => ({ ...prev, sellingPrice: '', calculatedPrice: '' }));
                        return;
                      }
                      const newPrice = parseFloat(rawValue);
                      if (isNaN(newPrice)) {
                        setCurrentItem(prev => ({ ...prev, calculatedPrice: rawValue }));
                        return;
                      }
                      setCurrentItem(prev => ({
                        ...prev,
                        sellingPrice: newPrice / unitMultiplier(prev.unit),
                        calculatedPrice: rawValue
                      }));
                    }}
                    min="0"
                    step="any"
                    onWheel={(e) => e.target.blur()}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="add-product-btn-container">
                <button
                  type="button"
                  className="add-product-btn"
                  onClick={addSalesItem}
                  disabled={isSubmitting || !currentItem.productId || !currentItem.quantity || !currentItem.sellingPrice}
                >
                  <FiPlus /> Add Product
                </button>
              </div>
            </div>
          )}
        </div>

        {salesItems.length > 0 && (
          <div className="form-section">
            <h2 className="section-title">Sale Items</h2>
            <table className="added-products-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Unit Price (Rs)</th>
                  <th>Total (Rs)</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {salesItems.map((item, index) => {
                  const multiplier = unitMultiplier(item.unit);
                  const normalizedPrice = (parseFloat(item.selling_price) || 0) * multiplier;
                  const lineTotal = normalizedPrice * (parseFloat(item.quantity) || 0);
                  return (
                    <tr key={item.sale_id || `new-${index}`}>
                      <td>
                        {item.item_name}
                        {!item.sale_id && <FiEdit2 title="New item" style={{ marginLeft: '0.4rem', color: '#4299e1' }} />}
                      </td>
                      <td>
                        <input
                          type="number"
                          className={`inline-edit-input ${errors[`quantity_${index}`] ? 'error-input' : ''}`}
                          value={item.quantity}
                          onChange={(e) => updateLineQuantity(index, e.target.value)}
                          min="1"
                          onWheel={(e) => e.target.blur()}
                          disabled={isSubmitting}
                        />
                        {errors[`quantity_${index}`] && <span className="error-message">{errors[`quantity_${index}`]}</span>}
                      </td>
                      <td>{item.unit}</td>
                      <td>
                        <input
                          type="number"
                          className={`inline-edit-input ${errors[`price_${index}`] ? 'error-input' : ''}`}
                          value={normalizedPrice}
                          onChange={(e) => updateLineNormalizedPrice(index, e.target.value)}
                          min="0"
                          step="0.01"
                          onWheel={(e) => e.target.blur()}
                          disabled={isSubmitting}
                        />
                        {errors[`price_${index}`] && <span className="error-message">{errors[`price_${index}`]}</span>}
                      </td>
                      <td>{lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td>
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => removeSalesItem(index)}
                          disabled={isSubmitting}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4" className="total-label">Subtotal:</td>
                  <td className="total-amount">
                    Rs{calculateTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {salesItems.length > 0 && (
          <div className="form-section">
            <h2 className="section-title">Discount</h2>
            <div className="form-group">
              <label htmlFor="discountType">Discount Type</label>
              <select
                id="discountType"
                name="discountType"
                value={formData.discountType}
                onChange={handleFormChange}
                disabled={isSubmitting}
              >
                <option value="amount">Rs (Fixed Amount)</option>
                <option value="percentage">% (Percentage)</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="discountValue">
                Discount Value {formData.discountType === 'percentage' ? '(%)' : '(Rs)'}
              </label>
              <input
                type="number"
                id="discountValue"
                name="discountValue"
                value={formData.discountValue}
                onChange={handleFormChange}
                min="0"
                max={formData.discountType === 'percentage' ? 100 : undefined}
                step="any"
                onWheel={(e) => e.target.blur()}
                disabled={isSubmitting}
                placeholder="0"
              />
            </div>
          </div>
        )}

        <div className="form-section">
          <h2 className="section-title">Payment Information</h2>

          <div className="form-group">
            <label htmlFor="paymentType">Payment Method *</label>
            <select
              id="paymentType"
              name="paymentType"
              value={formData.paymentType}
              onChange={handleFormChange}
              disabled={isSubmitting}
            >
              <option value="cash">💵 Cash</option>
              <option value="bank">🏦 Bank / Transfer</option>
              <option value="cheque">📋 Cheque</option>
            </select>
          </div>

          {formData.paymentType === 'bank' && (
            <div className={`form-group ${errors.bankAccountId ? 'has-error' : ''}`}>
              <label htmlFor="bankAccountId">Transfer To Bank Account *</label>
              <select
                id="bankAccountId"
                name="bankAccountId"
                value={formData.bankAccountId}
                onChange={handleFormChange}
                disabled={isSubmitting || bankAccounts.length === 0}
                className={errors.bankAccountId ? 'error-input' : ''}
              >
                <option value="">Select bank account</option>
                {bankAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.account_name} - {account.account_number}
                  </option>
                ))}
              </select>
              {errors.bankAccountId && <span className="error-message">{errors.bankAccountId}</span>}
            </div>
          )}

          <div className={`form-group ${errors.paidAmount ? 'has-error' : ''}`}>
            <label htmlFor="paidAmount">Paid Amount (Rs) *</label>
            <input
              type="number"
              id="paidAmount"
              name="paidAmount"
              value={formData.paidAmount}
              onChange={handleFormChange}
              step="any"
              min="0"
              onWheel={(e) => e.target.blur()}
              max={calculateNetTotal()}
              className={errors.paidAmount ? 'error-input' : ''}
              disabled={isSubmitting}
            />
            {errors.paidAmount && <span className="error-message">{errors.paidAmount}</span>}
          </div>

          <div className="payment-summary">
            <div className="payment-summary-row">
              <span>Subtotal:</span>
              <span>Rs{calculateTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            {calculateDiscountAmount() > 0 && (
              <div className="payment-summary-row">
                <span>Discount:</span>
                <span>-Rs{calculateDiscountAmount().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            <div className="payment-summary-row">
              <span>Total Amount:</span>
              <span>Rs{calculateNetTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="payment-summary-row">
              <span>Paid Amount:</span>
              <span>Rs{parseFloat(formData.paidAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="payment-summary-row credit">
              <span>Credit Amount:</span>
              <span>Rs{Math.max(0, calculateNetTotal() - parseFloat(formData.paidAmount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-btn"
            disabled={isSubmitting || salesItems.length === 0}
          >
            <FiSave /> {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditSales;
